import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import useFaceScanStore from 'store/face-scan';
import { ScanImageCaptureConfig } from './scan-config';
import { getFaceScanMediaUploadUrls } from 'services/face-scan/api';

export type ScanImageCaptureAndUploadState = {
  capturedImageDataUrls: string[];
  primaryCapturedImageDataUrl: string | null;
  primaryUploadedFileUrl: string | null;
  isUploading: boolean;
  uploadError: unknown | null;
};

export function useScanImageCaptureAndUpload({
  videoRef,
  isMeasuring,
  durationSeconds,
  config,
  readingId,
  resetKey,
}: {
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  isMeasuring: boolean;
  durationSeconds: number;
  config: ScanImageCaptureConfig;
  readingId: string | null | undefined;
  resetKey?: string;
}): ScanImageCaptureAndUploadState {
  const capturedAtSecondsRef = useRef<Set<number>>(new Set());
  const [capturedImageDataUrls, setCapturedImageDataUrls] = useState<string[]>([]);

  const [primaryUploadedFileUrl, setPrimaryUploadedFileUrl] = useState<string | null>(
    null
  );
  const uploadedForReadingIdRef = useRef<string | null>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<unknown | null>(null);

  const primaryCapturedImageDataUrl = capturedImageDataUrls[0] ?? null;

  const captureFirstVideoFrame = useCallback((): string | null => {
    const video = videoRef.current;
    if (!video) return null;

    if (video.readyState < 2) return null;

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.translate(width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, width, height);

    return canvas.toDataURL('image/jpeg', 0.9);
  }, [videoRef]);

  const setPresignedUrls = useFaceScanStore((s) => s.setPresignedUrls);

  const uploadImageAndGetPresignedUrls = useCallback(
    async (dataUrl: string) => {
      if (!readingId) return null;

      const blob = await (await fetch(dataUrl)).blob();
      const urls = await getFaceScanMediaUploadUrls(readingId, blob);
      setPresignedUrls({
        video: urls.upload_url,
        json: urls.json_upload_url,
      });
      return urls.upload_url;
    },
    [readingId, setPresignedUrls]
  );

  useEffect(() => {
    capturedAtSecondsRef.current = new Set();
    setCapturedImageDataUrls([]);
    setPrimaryUploadedFileUrl(null);
    uploadedForReadingIdRef.current = null;
    setIsUploading(false);
    setUploadError(null);
  }, [resetKey]);

  useEffect(() => {
    if (!isMeasuring) return;

    const maxImages = Math.max(0, config?.maxImages || 0);
    if (maxImages === 0) return;

    const targets = (config?.captureAtSeconds || [])
      .map((s) => Math.floor(Number(s)))
      .filter((s) => Number.isFinite(s) && s >= 0)
      .sort((a, b) => a - b)
      .slice(0, maxImages);

    if (!targets.length) return;

    if (!targets.includes(durationSeconds)) return;
    if (capturedAtSecondsRef.current.has(durationSeconds)) return;
    if (capturedAtSecondsRef.current.size >= maxImages) return;

    capturedAtSecondsRef.current.add(durationSeconds);

    (async () => {
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

      try {
        const dataUrl = captureFirstVideoFrame();
        if (!dataUrl) return;

        setCapturedImageDataUrls((prev) =>
          prev.length >= maxImages ? prev : [...prev, dataUrl]
        );

        if (readingId && uploadedForReadingIdRef.current !== readingId) {
          setIsUploading(true);
          setUploadError(null);
          try {
            const uploadedUrl = await uploadImageAndGetPresignedUrls(dataUrl);
            if (uploadedUrl) {
              setPrimaryUploadedFileUrl(uploadedUrl);
              uploadedForReadingIdRef.current = readingId;
            }
          } catch (e) {
            setUploadError(e);
          } finally {
            setIsUploading(false);
          }
        }
      } catch (e) {
        setUploadError(e);
        setIsUploading(false);
      }
    })();
  }, [
    captureFirstVideoFrame,
    config,
    durationSeconds,
    isMeasuring,
    readingId,
    uploadImageAndGetPresignedUrls,
  ]);

  return {
    capturedImageDataUrls,
    primaryCapturedImageDataUrl,
    primaryUploadedFileUrl,
    isUploading,
    uploadError,
  };
}
